import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowLeft, Save, Send, CheckCircle, Download, Eye, EyeOff, FileText, Loader, AlertCircle, XCircle, Columns, Pencil, MessageSquare, X, AlertTriangle, ChevronDown, RotateCcw } from 'lucide-react';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import { RootState } from '../store/reducers';
import {
  fetchDecisionDetailRequest,
  clearDecisionDetail,
  updateDecisionRequest,
  workflowActionRequest,
} from '../store/decisions/actions';
import { signalDecision } from '../store/decisions/service';
import './DecisionDetail.css';

const entityColors: Record<string, { color: string; bg: string; label: string }> = {
  PARTIE: { color: '#8B6914', bg: '#FFF8E7', label: 'Partie' },
  LIEU: { color: '#2563EB', bg: '#EFF6FF', label: 'Lieu' },
  SOCIETE: { color: '#7C3AED', bg: '#F5F3FF', label: 'Société' },
  MONTANT: { color: '#059669', bg: '#ECFDF5', label: 'Montant' },
  CONSEIL: { color: '#DC2626', bg: '#FEF2F2', label: 'Conseil' },
  DATE: { color: '#C4A535', bg: '#FFF8E7', label: 'Date' },
  ADRESSE: { color: '#0891B2', bg: '#ECFEFF', label: 'Adresse' },
};

// Workflow: human transitions per statut
// Pipeline auto after upload: brouillon → extraction_api → anonymisation → resume_genere → a_verifier
// Then human: a_verifier → valide → publie | rejete
const workflowActions: Record<string, Array<{ action: 'soumettre' | 'valider' | 'publier' | 'rejeter' | 'remettre_en_verification'; label: string; icon: any; primary?: boolean; danger?: boolean }>> = {
  brouillon:  [{ action: 'soumettre', label: 'Soumettre', icon: Send }],
  a_verifier: [{ action: 'valider', label: 'Valider', icon: CheckCircle, primary: true }],
  valide:     [
    { action: 'publier', label: 'Publier', icon: CheckCircle, primary: true },
    { action: 'rejeter', label: 'Rejeter', icon: XCircle, danger: true },
  ],
  rejete:     [{ action: 'remettre_en_verification', label: 'Restaurer', icon: RotateCcw, primary: true }],
};

const DecisionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const isAdmin = location.pathname.startsWith('/espace-admin');
  const decisionsListPath = isAdmin ? '/espace-admin/decisions' : '/espace-tca/decisions';

  const { detail, detailLoading, detailError, workflowLoading, isSubmitting } = useSelector(
    (state: RootState) => state.decisions
  );

  const [showOriginal, setShowOriginal] = useState(false);
  const [keywords, setKeywords] = useState<string[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [editedMarginContent, setEditedMarginContent] = useState('');
  const editableRef = useRef<HTMLDivElement>(null);

  // Signalement modal state
  const [showSignalModal, setShowSignalModal] = useState(false);
  const [signalType, setSignalType] = useState('');
  const [signalDescription, setSignalDescription] = useState('');
  const [signalSubmitting, setSignalSubmitting] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchDecisionDetailRequest(id));
    }
    return () => {
      dispatch(clearDecisionDetail());
    };
  }, [dispatch, id]);


  // Sync keywords from API detail
  useEffect(() => {
    if (detail?.mots_cles) {
      setKeywords(detail.mots_cles);
    }
  }, [detail?.mots_cles]);

  // Sync editedContent from API detail - utilise contenu_corps_anonymise si disponible
  useEffect(() => {
    if (detail?.contenu_corps_anonymise) {
      setEditedContent(detail.contenu_corps_anonymise);
    } else if (detail?.contenu_anonymise) {
      setEditedContent(detail.contenu_anonymise);
    }
  }, [detail?.contenu_corps_anonymise, detail?.contenu_anonymise]);

  // Sync margin content from API detail
  useEffect(() => {
    if (detail?.contenu_marge_anonymise) {
      setEditedMarginContent(detail.contenu_marge_anonymise);
    }
  }, [detail?.contenu_marge_anonymise]);

  // Format legal text: API often returns raw OCR text (one continuous string).
  // This adds structure: section headers, paragraph breaks, etc.
  const formatLegalText = (text: string): string => {
    if (!text) return '';
    let t = text;

    // If text already has line breaks, do light preprocessing only
    if (t.includes('\n')) {
      t = t.replace(/\*\*\s+\*\*/g, '**\n\n**');
      t = t.replace(/\*\*\s+(?=[^*\n])/g, '**\n\n');
      t = t.replace(/([^*\n])\s+\*\*/g, '$1\n\n**');
      t = t.replace(/\s*;\s+-\s+/g, ';\n- ');
      t = t.replace(/:\s+-\s+/g, ':\n- ');
      return t;
    }

    // --- Plain text from OCR: add structure for legal documents ---

    // 1. Major legal section headers → ### HEADER with separator
    const majorSections = [
      'PAR CES MOTIFS',
      'ENSEMBLE FAITS ET PROCEDURE',
      'DES MOTIFS EN LA FORME',
      'LE TRIBUNAL',
      'AU FOND',
      'EN LA FORME',
      'DES MOTIFS',
    ];
    majorSections.sort((a, b) => b.length - a.length);
    for (const sec of majorSections) {
      const esc = sec.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      t = t.replace(new RegExp(`\\s+(${esc})\\s+`, 'g'), '\n\n---\n\n### $1\n\n');
    }

    // 2. Sub-section headers → #### HEADER
    t = t.replace(/\s+(Sur l[''\u2019]action publique)/g, '\n\n#### $1\n\n');
    t = t.replace(/\s+(Sur les dépens)/g, '\n\n#### $1\n\n');

    // 3. DECISION header
    t = t.replace(/\s*(DECISION\s*:)\s*/g, '\n\n### $1\n\n');

    // 4. Paragraph breaks after semicolons
    t = t.replace(/;\s+/g, ';\n\n');

    // 5. Legal clause starters on new paragraphs
    t = t.replace(/\s+(Attendu que\b)/g, '\n\n$1');
    t = t.replace(/\s+(Qu[''\u2019]il\b)/g, '\n\n$1');
    t = t.replace(/\s+(Que (?:les? |la |dans |l[''\u2019]))/g, '\n\n$1');

    // 6. Parties layout
    t = t.replace(/\s+(Entre\s*:)/g, '\n\n$1');
    t = t.replace(/\s+(D[''\u2019]une part)/g, '\n\n*$1*');
    t = t.replace(/\s+(D[''\u2019]autre part)/g, '\n\n*$1*');

    // 7. Condamnation details on new lines
    t = t.replace(/\s+(Condamnation\s*:)/g, '\n\n$1');
    t = t.replace(/\s+(Peines principales\s*:)/g, '\n\n$1');
    t = t.replace(/\s+(Peines complémentaires[^:]*:)/g, '\n\n$1');
    t = t.replace(/\s+(Emprisonnement\s*:)/g, '\n\n$1');
    t = t.replace(/\s+(Amende\s*:)/g, '\n\n$1');
    t = t.replace(/\s+(Dommages-intérêts\s*:)/g, '\n\n$1');

    // 8. Judgment preamble: judge list (", M. " or ", Mme " → line break)
    t = t.replace(/,\s+((?:M\.|Mme|Me)\s)/g, ',\n\n$1');

    // 9. "Statuant publiquement" starts a new block
    t = t.replace(/\s+(Statuant publiquement)/g, '\n\n$1');
    t = t.replace(/\s+(Déclare\b)/g, '\n\n$1');
    t = t.replace(/\s+(En répression)/g, '\n\n$1');
    t = t.replace(/\s+(Décerne\b)/g, '\n\n$1');
    t = t.replace(/\s+(Le condamne\b)/g, '\n\n$1');

    // 10. Handle bold markers if present
    t = t.replace(/\*\*\s+\*\*/g, '**\n\n**');

    // 11. Clean excessive newlines
    t = t.replace(/\n{3,}/g, '\n\n');

    return t;
  };

  // Entity tag popover handlers (disabled for now)
  // const handleTagClick = (tag: string, e: React.MouseEvent) => {
  //   if (isEditing) return;
  //   e.stopPropagation();
  //   const rect = (e.target as HTMLElement).getBoundingClientRect();
  //   setTagPopover({
  //     text: tag,
  //     rect: { top: rect.bottom + 4, left: rect.left },
  //   });
  //   setTagReplacement('');
  // };

  // const handleTagReplace = () => {
  //   if (!tagPopover) return;
  //   const replacement = tagReplacement.trim();
  //   if (!replacement) return;
  //   setEditedContent(prev => prev.split(tagPopover.text).join(replacement));
  //   setTagPopover(null);
  // };

  // const handleTagRemove = () => {
  //   if (!tagPopover) return;
  //   setEditedContent(prev => prev.split(tagPopover.text).join(''));
  //   setTagPopover(null);
  // };

  const getEntityStyle = (tagContent: string): { color: string; bg: string } => {
    // Check entityColors map first (for [PARTIE], [LIEU], etc.)
    const baseType = tagContent.replace(/_\d+$/, '');
    if (entityColors[baseType]) {
      return { color: entityColors[baseType].color, bg: entityColors[baseType].bg };
    }
    // Handle anonymized patterns like "NOM ANONYMISÉ", "ADRESSE ANONYMISÉE"
    const upper = tagContent.toUpperCase();
    if (upper.includes('NOM') || upper.includes('PARTIE')) return { color: '#6B5010', bg: '#FFF8E7' };
    if (upper.includes('ADRESSE')) return { color: '#0E7490', bg: '#ECFEFF' };
    if (upper.includes('LIEU')) return { color: '#1D4ED8', bg: '#EFF6FF' };
    if (upper.includes('DATE')) return { color: '#B45309', bg: '#FFFBEB' };
    if (upper.includes('MONTANT')) return { color: '#047857', bg: '#ECFDF5' };
    if (upper.includes('SOCIETE') || upper.includes('SOCIÉTÉ')) return { color: '#6D28D9', bg: '#F5F3FF' };
    if (upper.includes('CONSEIL')) return { color: '#DC2626', bg: '#FEF2F2' };
    // Default for any [TAG]
    return { color: '#6B5010', bg: '#FFF8E7' };
  };

  const highlightEntities = (text: string): React.ReactNode[] => {
    // Match [TAG] patterns: uppercase start, may contain accented chars/spaces/underscores
    // Handles both [PARTIE], [LIEU_1] and [NOM ANONYMISÉ], [ADRESSE ANONYMISÉE]
    const regex = /\[[A-Z\u00C0-\u00DC\u00C9][A-Z\u00C0-\u00DC\u00C9a-z\u00E0-\u00FC\u00E9 _'0-9]+\]/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      const tag = match[0];
      const inner = tag.slice(1, -1);
      const style = getEntityStyle(inner);
      parts.push(
        <span
          key={match.index}
          className="entity-tag"
          style={{ color: style.color, background: style.bg }}
        >
          {tag}
        </span>
      );
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts;
  };

  // Walk ReactMarkdown children and apply entity highlighting to string nodes
  const withEntityHighlight = (children: React.ReactNode): React.ReactNode => {
    return React.Children.map(children, (child) => {
      if (typeof child === 'string') {
        return <>{highlightEntities(child)}</>;
      }
      return child;
    });
  };

  // Custom components for ReactMarkdown with entity highlighting
  const entityMdComponents: Record<string, React.FC<any>> = {
    p: ({ children }: any) => <p>{withEntityHighlight(children)}</p>,
    strong: ({ children }: any) => <strong>{withEntityHighlight(children)}</strong>,
    em: ({ children }: any) => <em>{withEntityHighlight(children)}</em>,
    li: ({ children }: any) => <li>{withEntityHighlight(children)}</li>,
    h1: ({ children }: any) => <h1>{withEntityHighlight(children)}</h1>,
    h2: ({ children }: any) => <h2>{withEntityHighlight(children)}</h2>,
    h3: ({ children }: any) => <h3>{withEntityHighlight(children)}</h3>,
    h4: ({ children }: any) => <h4>{withEntityHighlight(children)}</h4>,
    td: ({ children }: any) => <td>{withEntityHighlight(children)}</td>,
  };

  // Convert formatted markdown text to HTML for contentEditable WYSIWYG editing
  const markdownToHtml = (md: string): string => {
    const entityRegex = /\[[A-Z\u00C0-\u00DC\u00C9][A-Z\u00C0-\u00DC\u00C9a-z\u00E0-\u00FC\u00E9 _'0-9]+\]/g;
    const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const applyInline = (text: string): string => {
      let r = text.replace(entityRegex, (match) => {
        const inner = match.slice(1, -1);
        const st = getEntityStyle(inner);
        return `<span class="entity-tag" style="color:${st.color};background:${st.bg}">${match}</span>`;
      });
      r = r.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      r = r.replace(/\*(.+?)\*/g, '<em>$1</em>');
      return r;
    };

    const lines = md.split('\n');
    const blocks: string[] = [];
    let currentP: string[] = [];

    const flushP = () => {
      if (currentP.length > 0) {
        blocks.push(`<p>${currentP.join('<br>')}</p>`);
        currentP = [];
      }
    };

    for (const rawLine of lines) {
      const line = rawLine.trim();
      if (!line) { flushP(); continue; }

      const isHr = line === '---';
      const isH4 = line.startsWith('#### ');
      const isH3 = !isH4 && line.startsWith('### ');
      const isList = line.startsWith('- ');

      if (isHr) {
        flushP();
        blocks.push('<hr>');
      } else if (isH4) {
        flushP();
        blocks.push(`<h4>${applyInline(esc(line.slice(5)))}</h4>`);
      } else if (isH3) {
        flushP();
        blocks.push(`<h3>${applyInline(esc(line.slice(4)))}</h3>`);
      } else if (isList) {
        flushP();
        blocks.push(`<p style="padding-left:20px">\u2022 ${applyInline(esc(line.slice(2)))}</p>`);
      } else {
        currentP.push(applyInline(esc(line)));
      }
    }

    flushP();
    return blocks.join('');
  };

  // Initialize contentEditable HTML when entering edit mode
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isEditing && editableRef.current) {
      const formatted = formatLegalText(editedContent);
      editableRef.current.innerHTML = markdownToHtml(formatted);
    }
  }, [isEditing, showOriginal]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEditableInput = () => {
    if (!editableRef.current) return;
    setEditedContent(editableRef.current.innerText);
  };

  const handleEditablePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  // Détection des changements - compare avec contenu_corps ou contenu_anonymise
  const hasContentChanges = detail?.contenu_corps_anonymise
    ? editedContent !== (detail.contenu_corps_anonymise || '')
    : editedContent !== (detail?.contenu_anonymise || '');

  const hasMarginChanges = detail?.contenu_marge_anonymise
    ? editedMarginContent !== (detail.contenu_marge_anonymise || '')
    : false;

  const handleSave = () => {
    if (!detail) return;
    const payload: Record<string, any> = { mots_cles: keywords };

    // Sauvegarder le contenu du corps (ou le contenu complet si pas de séparation)
    if (hasContentChanges) {
      if (detail.contenu_corps_anonymise !== undefined) {
        payload.contenu_corps_anonymise = editedContent;
      } else {
        payload.contenu_anonymise = editedContent;
      }
    }

    // Sauvegarder le contenu de la marge si modifié
    if (hasMarginChanges) {
      payload.contenu_marge_anonymise = editedMarginContent;
    }

    dispatch(updateDecisionRequest(detail.id, payload));
  };

  const handleWorkflowAction = (action: 'soumettre' | 'valider' | 'publier' | 'rejeter' | 'remettre_en_verification') => {
    if (!detail) return;
    dispatch(workflowActionRequest(detail.id, action, () => {
      // Detail will be refreshed via reducer
    }));
  };

  const handleViewPdf = () => {
    if (!detail) return;
    const url = detail.fichier_anonymise_presigned_url || detail.fichier_presigned_url;
    if (url) {
      window.open(url, '_blank');
    } else {
      toast.info('Aucun fichier PDF disponible pour cette décision.');
    }
  };

  const handleExport = () => {
    if (!detail) return;
    const url = detail.fichier_anonymise_presigned_url || detail.fichier_presigned_url;
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.download = `decision_${detail.numero || detail.id}.pdf`;
      link.click();
    } else {
      toast.info('Aucun fichier disponible à exporter.');
    }
  };

  const handleSignalSubmit = async () => {
    if (!detail || !signalType) return;
    setSignalSubmitting(true);
    try {
      await signalDecision({
        decision: detail.id,
        commentaire: signalDescription,
        type_probleme: signalType,
      });
      toast.success('Signalement envoyé avec succès.');
      setShowSignalModal(false);
      setSignalType('');
      setSignalDescription('');
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de l\'envoi du signalement.');
    } finally {
      setSignalSubmitting(false);
    }
  };

  // Loading state
  if (detailLoading) {
    return (
      <div className="decision-detail">
        <div className="detail-loading">
          <Loader size={32} className="detail-spinner" />
          <span>Chargement de la décision...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (detailError) {
    return (
      <div className="decision-detail">
        <div className="detail-error">
          <AlertCircle size={32} />
          <span>Erreur lors du chargement de la décision.</span>
          <button className="btn-detail-secondary" onClick={() => navigate(decisionsListPath)}>
            Retour aux décisions
          </button>
        </div>
      </div>
    );
  }

  // No data
  if (!detail) {
    return (
      <div className="decision-detail">
        <div className="detail-error">
          <span>Décision non trouvée.</span>
          <button className="btn-detail-secondary" onClick={() => navigate(decisionsListPath)}>
            Retour aux décisions
          </button>
        </div>
      </div>
    );
  }

  const d = detail;
  const availableActions = workflowActions[d.statut] || [];

  // Render sidebar content (editable or not)
  const renderSidebarContent = () => {
    // Si contenu_marge_anonymise existe, on l'affiche comme du texte
    if (d.contenu_marge_anonymise) {
      const lines = editedMarginContent.split('\n');
      return (
        <div className="sidebar-margin-content">
          {lines.map((line, index) => {
            const trimmedLine = line.trim();

            // Ligne vide crée un espace
            if (!trimmedLine) {
              return <div key={index} className="sidebar-margin-line" style={{ marginBottom: '10px' }}>&nbsp;</div>;
            }

            // Séparateurs (lignes de tirets)
            if (trimmedLine.match(/^[-─━]{3,}$/)) {
              return <div key={index} className="sidebar-margin-separator" />;
            }

            // En-têtes principaux (REPUBLIQUE, COUR D'APPEL, TRIBUNAL)
            if (trimmedLine.match(/^(REPUBLIQUE|COUR D['']APPEL|TRIBUNAL)/i)) {
              return (
                <div key={index} className="sidebar-margin-line" style={{
                  fontWeight: 700,
                  fontSize: '12px',
                  marginBottom: '4px',
                  letterSpacing: '0.5px',
                  lineHeight: '1.3'
                }}>
                  {line}
                </div>
              );
            }

            // Numéros de jugement/parquet (style orange)
            if (trimmedLine.match(/^N°.*\d+/i)) {
              return (
                <div key={index} className="sidebar-margin-line" style={{
                  fontWeight: 700,
                  fontSize: '12px',
                  color: '#8B6914',
                  marginBottom: '3px',
                  lineHeight: '1.3'
                }}>
                  {line}
                </div>
              );
            }

            // Mots-clés importants (MINISTERE, Contre, Nature, Décision, etc.)
            if (trimmedLine.match(/^(LE MINISTERE|Contre|Nature|Décision|Peines|Dommages)/i)) {
              return (
                <div key={index} className="sidebar-margin-line" style={{
                  fontWeight: 700,
                  fontSize: '11px',
                  marginBottom: '3px',
                  textDecoration: trimmedLine === 'Contre' ? 'none' : 'underline',
                  textDecorationThickness: '1px'
                }}>
                  {line}
                </div>
              );
            }

            // Titres en majuscules (autres que les en-têtes principaux)
            if (trimmedLine === trimmedLine.toUpperCase() && trimmedLine.length > 5) {
              return (
                <div key={index} className="sidebar-margin-line" style={{
                  fontWeight: 700,
                  fontSize: '11px',
                  marginBottom: '3px',
                  letterSpacing: '0.3px'
                }}>
                  {line}
                </div>
              );
            }

            // Lignes normales
            return (
              <div key={index} className="sidebar-margin-line" style={{
                fontSize: '11px',
                fontWeight: 500,
                lineHeight: '1.4'
              }}>
                {line}
              </div>
            );
          })}
        </div>
      );
    }

    // Pas de marge si contenu_marge_anonymise n'existe pas
    return null;
  };

  return (
    <div className="decision-detail">
      {/* Header */}
      <div className="detail-header">
        <div className="detail-header-left">
          <button className="btn-back" onClick={() => navigate(decisionsListPath)}>
            <ArrowLeft size={18} />
          </button>
          <div className="detail-breadcrumb">
            <span className="breadcrumb-link" onClick={() => navigate(decisionsListPath)}>Décisions</span>
            <span className="breadcrumb-sep">/</span>
            <span className="breadcrumb-current">{d.numero || d.id}</span>
          </div>
        </div>
        <div className="detail-header-actions">
          <button className={`btn-detail-secondary ${(hasContentChanges || hasMarginChanges) ? 'btn-unsaved' : ''}`} onClick={handleSave} disabled={isSubmitting}>
            <Save size={14} /> Enregistrer {(hasContentChanges || hasMarginChanges) && <span className="unsaved-dot" />}
          </button>
          {availableActions.map(wa => (
            <button
              key={wa.action}
              className={wa.danger ? 'btn-detail-danger' : wa.primary ? 'btn-detail-primary' : 'btn-detail-secondary'}
              onClick={() => handleWorkflowAction(wa.action)}
              disabled={workflowLoading}
            >
              <wa.icon size={14} /> {workflowLoading ? 'En cours...' : wa.label}
            </button>
          ))}
          <button className="btn-detail-secondary" onClick={handleExport}>
            <Download size={14} /> Exporter
          </button>
        </div>
      </div>

      {/* Document layout */}
      <div className="detail-columns">
        <div className="detail-card document-card">
          <div className="document-header">
            <h4 className="detail-card-title">
              {showOriginal ? 'Comparaison' : 'Document anonymisé'}
            </h4>
            <div className="document-header-actions">
                {!showOriginal && editedContent && (
                  <button
                    className={`btn-toggle-doc ${isEditing ? 'btn-toggle-doc-active' : ''}`}
                    onClick={() => setIsEditing(!isEditing)}
                  >
                    {isEditing ? <Eye size={14} /> : <Pencil size={14} />}
                    {isEditing ? 'Aperçu' : 'Modifier'}
                  </button>
                )}
                <button className="btn-view-pdf" onClick={handleViewPdf}>
                  <FileText size={14} /> Afficher le PDF
                </button>
                {d.fichier_presigned_url && d.contenu_anonymise && (
                  <button
                    className={`btn-toggle-doc ${showOriginal ? 'btn-toggle-doc-active' : ''}`}
                    onClick={() => { setShowOriginal(!showOriginal); setIsEditing(false); }}
                  >
                    {showOriginal ? <EyeOff size={14} /> : <Columns size={14} />}
                    {showOriginal ? "Fermer la comparaison" : "Voir l'original"}
                  </button>
                )}
                <button
                  className="btn-toggle-doc"
                  onClick={() => setShowSignalModal(true)}
                >
                  <MessageSquare size={14} /> Signaler
                </button>
              </div>
            </div>

          {/* Container avec marge + contenu */}
          <div className="document-with-margin">
            {/* Sidebar - Marge avec informations clés (cachée en mode comparaison) */}
            {!showOriginal && d.contenu_marge_anonymise && (
            <div className="decision-sidebar">
              {renderSidebarContent()}
            </div>
            )}

            {/* Document content */}
            <div className="document-content-area">
            {!showOriginal && d.entites_masquees && Object.keys(d.entites_masquees).length > 0 && (
              <div className="entity-legend">
                {Object.keys(d.entites_masquees).map(key => {
                  const baseType = key.replace(/_\d+$/, '');
                  const style = entityColors[baseType] || entityColors[key] || { color: '#666', bg: '#f0f0f0', label: key };
                  return (
                    <span key={key} className="entity-legend-item" style={{ color: style.color, background: style.bg }}>
                      {style.label} ({(d.entites_masquees[key] || []).length})
                    </span>
                  );
                })}
              </div>
            )}

            {showOriginal ? (
              <div className="compare-container">
                {/* Left: Original PDF */}
                <div className="compare-panel">
                  <div className="compare-panel-header compare-panel-header-original">
                    <FileText size={14} />
                    <span>Document original (PDF)</span>
                  </div>
                  <div className="compare-panel-body compare-pdf-body">
                    {d.fichier_presigned_url ? (
                      <iframe
                        src={d.fichier_presigned_url}
                        className="compare-pdf-iframe"
                        title="PDF Original"
                      />
                    ) : (
                      <p className="document-empty">Aucun fichier PDF disponible.</p>
                    )}
                  </div>
                </div>

                {/* Divider */}
                <div className="compare-divider" />

                {/* Right: Anonymisé */}
                <div className="compare-panel">
                  <div className="compare-panel-header compare-panel-header-anonymise">
                    <Eye size={14} />
                    <span>Document anonymisé</span>
                    {editedContent && (
                      <button
                        className={`btn-toggle-doc btn-toggle-doc-sm ${isEditing ? 'btn-toggle-doc-active' : ''}`}
                        onClick={() => setIsEditing(!isEditing)}
                      >
                        {isEditing ? <Eye size={13} /> : <Pencil size={13} />}
                        {isEditing ? 'Aperçu' : 'Modifier'}
                      </button>
                    )}
                  </div>

                  {/* Container avec marge + contenu pour la comparaison */}
                  <div className="compare-with-margin">
                    {/* Sidebar - Marge (seulement si elle existe) */}
                    {d.contenu_marge_anonymise && (
                    <div className="decision-sidebar decision-sidebar-compare">
                      {renderSidebarContent()}
                    </div>
                    )}

                    {/* Contenu du document */}
                    <div className="compare-document-content">
                  {d.entites_masquees && Object.keys(d.entites_masquees).length > 0 && (
                    <div className="entity-legend">
                      {Object.keys(d.entites_masquees).map(key => {
                        const baseType = key.replace(/_\d+$/, '');
                        const style = entityColors[baseType] || entityColors[key] || { color: '#666', bg: '#f0f0f0', label: key };
                        return (
                          <span key={key} className="entity-legend-item" style={{ color: style.color, background: style.bg }}>
                            {style.label} ({(d.entites_masquees[key] || []).length})
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {isEditing ? (
                    <div
                      ref={editableRef}
                      className="document-text compare-panel-body document-text-editable"
                      contentEditable
                      suppressContentEditableWarning
                      onInput={handleEditableInput}
                      onPaste={handleEditablePaste}
                    />
                  ) : (
                    <div className="document-text compare-panel-body">
                      {editedContent ? (
                        <ReactMarkdown components={entityMdComponents}>
                          {formatLegalText(editedContent)}
                        </ReactMarkdown>
                      ) : (
                        <p className="document-empty">Contenu anonymisé non disponible.</p>
                      )}
                    </div>
                  )}
                    </div>
                  </div>
                </div>
              </div>
            ) : isEditing ? (
              <div
                ref={editableRef}
                className="document-text document-text-editable"
                contentEditable
                suppressContentEditableWarning
                onInput={handleEditableInput}
                onPaste={handleEditablePaste}
              />
            ) : (
              <div className="document-text">
                {editedContent ? (
                  <ReactMarkdown components={entityMdComponents}>
                    {formatLegalText(editedContent)}
                  </ReactMarkdown>
                ) : (
                  <p className="document-empty">
                    {d.contenu_original
                      ? "Le contenu anonymisé n'est pas encore disponible. Lancez l'anonymisation."
                      : "Aucun contenu textuel disponible. Consultez le PDF."
                    }
                  </p>
                )}
              </div>
            )}

            {/* Sticky save bar when editing */}
            {isEditing && (
              <div className="document-edit-bar">
                <span className="document-edit-bar-info">
                  <Pencil size={13} /> Mode édition
                  {hasContentChanges && <span className="unsaved-dot" />}
                </span>
                <div className="document-edit-bar-actions">
                  <button className="btn-edit-bar-cancel" onClick={() => setIsEditing(false)}>
                    Aperçu
                  </button>
                  <button
                    className={`btn-edit-bar-save ${hasContentChanges ? 'btn-edit-bar-save-active' : ''}`}
                    onClick={handleSave}
                    disabled={isSubmitting || !hasContentChanges}
                  >
                    <Save size={14} /> {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                </div>
              </div>
            )}

            {/* Tag edit popover (disabled for now)
            {tagPopover && (
              <div
                ref={popoverRef}
                className="tag-popover"
                style={{ top: tagPopover.rect.top, left: tagPopover.rect.left }}
              >
                <div className="tag-popover-header">
                  <span className="tag-popover-current">{tagPopover.text}</span>
                  <button className="tag-popover-close" onClick={() => setTagPopover(null)}>
                    <X size={14} />
                  </button>
                </div>
                <div className="tag-popover-body">
                  <label className="tag-popover-label">Remplacer par :</label>
                  <input
                    className="tag-popover-input"
                    value={tagReplacement}
                    onChange={(e) => setTagReplacement(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleTagReplace()}
                    placeholder="Nouveau texte..."
                    autoFocus
                  />
                </div>
                <div className="tag-popover-actions">
                  <button className="tag-popover-btn tag-popover-btn-primary" onClick={handleTagReplace} disabled={!tagReplacement.trim()}>
                    <RefreshCw size={12} /> Tout remplacer
                  </button>
                  <button className="tag-popover-btn tag-popover-btn-danger" onClick={handleTagRemove}>
                    <Trash2 size={12} /> Supprimer
                  </button>
                </div>
              </div>
            )}
            */}
            </div>
          </div>
        </div>

      </div>

      {/* Signal modal */}
      {showSignalModal && (
        <div className="signal-modal-overlay" onClick={() => !signalSubmitting && setShowSignalModal(false)}>
          <div className="signal-modal" onClick={(e) => e.stopPropagation()}>
            <button className="signal-modal-close" onClick={() => !signalSubmitting && setShowSignalModal(false)}>
              <X size={18} />
            </button>

            <div className="signal-modal-header">
              <div className="signal-modal-icon">
                <AlertTriangle size={22} />
              </div>
              <div>
                <h3 className="signal-modal-title">Signaler un problème sur ce document</h3>
                <p className="signal-modal-subtitle">
                  Décision <strong>{d.numero || d.id}</strong> — aidez-nous à améliorer la qualité en signalant toute erreur.
                </p>
              </div>
            </div>

            <div className="signal-modal-body">
              <div className="signal-modal-field">
                <label className="signal-modal-label">Type de problème</label>
                <div className="signal-modal-select-wrapper">
                  <select
                    className="signal-modal-select"
                    value={signalType}
                    onChange={(e) => setSignalType(e.target.value)}
                  >
                    <option value="">Sélectionner un type...</option>
                    <option value="erreur_anonymisation">Erreur d'anonymisation</option>
                    <option value="erreur_ocr">Erreur OCR</option>
                    <option value="metadonnee_incorrecte">Métadonnée incorrecte</option>
                    <option value="contenu_manquant">Contenu manquant</option>
                    <option value="autre">Autre</option>
                  </select>
                  <ChevronDown size={16} className="signal-modal-select-icon" />
                </div>
              </div>

              <div className="signal-modal-field">
                <label className="signal-modal-label">
                  Description
                  <span className="signal-modal-charcount">{signalDescription.length}/1000</span>
                </label>
                <textarea
                  className="signal-modal-textarea"
                  placeholder="Décrivez précisément l'erreur ou le problème constaté..."
                  value={signalDescription}
                  onChange={(e) => {
                    if (e.target.value.length <= 1000) setSignalDescription(e.target.value);
                  }}
                  rows={5}
                />
              </div>
            </div>

            <div className="signal-modal-actions">
              <button
                className="btn-signal-cancel"
                onClick={() => { setShowSignalModal(false); setSignalType(''); setSignalDescription(''); }}
                disabled={signalSubmitting}
              >
                Annuler
              </button>
              <button
                className="btn-signal-submit"
                onClick={handleSignalSubmit}
                disabled={!signalType || signalSubmitting}
              >
                {signalSubmitting && <Loader size={14} className="signal-spinner" />}
                {signalSubmitting ? 'Envoi en cours...' : 'Envoyer le signalement'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DecisionDetail;
